"use client";

import { Time } from "@internationalized/date";
import { type TimeValue, useTimeField } from "@react-aria/datepicker";
import { useObjectRef } from "@react-aria/utils";
import { type TimeFieldStateOptions, useTimeFieldState } from "@react-stately/datepicker";
import type { VariantProps } from "class-variance-authority";
import { type ChangeEvent, type ChangeEventHandler, type ReactNode, type Ref, useEffect } from "react";

import { cn } from "../../../utils";
import { useTextFieldRootContext } from "../context";
import { inputContainerStyles } from "../style";
import DateSegment from "./DateSegment";
import { dateSegmentContainerStyles } from "./style";

export type TextFieldInputTimeProps = Omit<
  TimeFieldStateOptions<TimeValue>,
  "value" | "defaultValue" | "onChange" | "locale" | "isDisabled"
> &
  VariantProps<typeof inputContainerStyles> &
  VariantProps<typeof dateSegmentContainerStyles> & {
    className?: string;
    containerClassName?: string;
    name?: string;
    value?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onAutocomplete?: (value?: string) => void;
    disabled?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
    ref?: Ref<HTMLInputElement>;
  };

const InputTime = ({
  className,
  containerClassName,
  variant = "outline",
  color = "primary",
  value,
  onChange,
  onAutocomplete,
  disabled,
  prefix,
  suffix,
  ref: forwardedRef,
  ...props
}: TextFieldInputTimeProps) => {
  const ref = useObjectRef(forwardedRef);
  const { setTextField } = useTextFieldRootContext<"input">();

  const state = useTimeFieldState({
    ...props,
    value: value
      ? new Time(Number(value.split(":")[0]), Number(value.split(":")[1]), Number(value.split(":")[2]))
      : undefined,
    onChange: (value: TimeValue | null) =>
      onChange &&
      onChange({
        target: { value: value?.toString() ?? undefined, name: props.name },
      } as ChangeEvent<HTMLInputElement>),
    isDisabled: disabled,
    label: true,
    locale: "en-US",
  });

  useEffect(() => {
    const formattedValue = state.dateFormatter.format(state.dateValue);
    const isInputCleared = formattedValue.startsWith("00:00") && !state.value;
    onAutocomplete?.(!isInputCleared ? formattedValue : undefined);
  }, [state.dateValue, state.value]);

  const textField = useTimeField(props, state, ref);
  const { fieldProps, isInvalid } = textField;

  useEffect(() => {
    setTextField(textField);
  }, [isInvalid]);

  useEffect(() => {
    // reset value
    if (!value) {
      state.setValue(null);
    }
  }, [value]);

  return (
    <div
      {...fieldProps}
      ref={ref}
      data-invalid={isInvalid}
      data-disabled={disabled}
      role="button"
      tabIndex={-1}
      className={cn(inputContainerStyles({ variant, color }), "outline-none", containerClassName)}
    >
      {prefix}

      <span className={cn(dateSegmentContainerStyles({ variant }), className, "-ml-0.5 whitespace-nowrap")}>
        {state.segments
          .filter(segment => segment.text)
          .map((segment, index) => (
            <DateSegment key={index} segment={segment} state={state} />
          ))}
      </span>

      {suffix}
    </div>
  );
};

export default InputTime;
